import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../hooks/use-toast';
import { apiRequest } from '../lib/queryClient';
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { UserPlus, Download, Upload, Eye, FileText, Trash2, Search, Mail, Phone, MapPin, Users, TrendingUp, DollarSign, Calendar, LayoutGrid, List as ListIcon, X, ChevronUp, ChevronDown, MoreVertical, Filter } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ClientForm from '../components/clients/client-form';
import ClientDetail from '../components/clients/client-detail';
import { formatPhone } from '../lib/validation';

interface Client {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  notes: string;
  createdAt: number;
  projects?: any[];
}

interface ClientWithProjects extends Client {
  projects?: any[];
}

interface ClientInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  notes: string;
}

export default function ClientsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('list');
  const [isClientFormOpen, setIsClientFormOpen] = useState(false);
  const [isClientDetailOpen, setIsClientDetailOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientWithProjects | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState<Set<number>>(new Set());
  const [sortBy, setSortBy] = useState<'name' | 'createdAt' | 'projects'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const getClientStatus = (c: ClientWithProjects): 'Active' | 'Inactive' | 'Onboarding' => {
    const hasProjects = (c.projects?.length ?? 0) > 0;
    const created = typeof c.createdAt === 'number' ? c.createdAt * 1000 : new Date(c.createdAt).getTime();
    const isNew = Date.now() - created < 30 * 24 * 60 * 60 * 1000;
    if (hasProjects) return 'Active';
    if (isNew) return 'Onboarding';
    return 'Inactive';
  };

  const formatJoinDate = (createdAt: number) => {
    try {
      const ts = typeof createdAt === 'number' && createdAt < 1e12 ? createdAt * 1000 : createdAt;
      return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return '—';
    }
  };

  const toggleSelectClient = (id: number) => {
    setSelectedClientIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedClientIds.size === filteredClients.length) {
      setSelectedClientIds(new Set());
    } else {
      setSelectedClientIds(new Set(filteredClients.map((c: Client) => c.id)));
    }
  };

  const clearSelection = () => setSelectedClientIds(new Set());

  // Fetch clients
  const { data: clients = [], isLoading: isLoadingClients } = useQuery({
    queryKey: ['/api/protected/clients'],
    queryFn: async () => {
      console.log('🔍 Clients page making API call to /api/protected/clients');
      const response = await apiRequest("GET", "/api/protected/clients");
      return await response.json();
    }
  });

  // Create client mutation
  const createClientMutation = useMutation({
    mutationFn: async (data: ClientInput) => {
      console.log('🔍 Creating client with data:', data);
      const response = await apiRequest("POST", "/api/protected/clients", data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/protected/clients'] });
      setIsClientFormOpen(false);
      toast({ title: 'Success', description: 'Client created successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  // Update client mutation
  const updateClientMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ClientInput }) => {
      console.log('🔍 Updating client:', id, data);
      const response = await apiRequest("PATCH", `/api/protected/clients?id=${id}`, data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/protected/clients'] });
      setIsClientFormOpen(false);
      setIsEditMode(false);
      toast({ title: 'Success', description: 'Client updated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  // Delete client mutation
  const deleteClientMutation = useMutation({
    mutationFn: async (id: number) => {
      console.log('🔍 Deleting client:', id);
      const response = await apiRequest("DELETE", `/api/protected/clients?id=${id}`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/protected/clients'] });
      setIsClientDetailOpen(false);
      toast({ title: 'Success', description: 'Client deleted successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  // Filter clients based on search query
  const filteredClients = clients.filter((client: Client) =>
    client.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (client.phone && client.phone.includes(searchQuery))
  );

  const sortedClients = React.useMemo(() => {
    const list = [...filteredClients];
    list.sort((a: ClientWithProjects, b: ClientWithProjects) => {
      let va: string | number, vb: string | number;
      if (sortBy === 'name') {
        va = `${a.firstName} ${a.lastName}`.toLowerCase();
        vb = `${b.firstName} ${b.lastName}`.toLowerCase();
      } else if (sortBy === 'createdAt') {
        va = typeof a.createdAt === 'number' ? a.createdAt : new Date(a.createdAt).getTime();
        vb = typeof b.createdAt === 'number' ? b.createdAt : new Date(b.createdAt).getTime();
      } else {
        va = a.projects?.length ?? 0;
        vb = b.projects?.length ?? 0;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredClients, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedClients.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedClients = sortedClients.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const toggleSort = (key: 'name' | 'createdAt' | 'projects') => {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const handleAddClient = () => {
    setIsEditMode(false);
    setSelectedClient(null);
    setIsClientFormOpen(true);
  };

  const handleEditClient = () => {
    setIsEditMode(true);
    setIsClientFormOpen(true);
  };

  const handleViewClientDetails = (client: ClientWithProjects) => {
    setSelectedClient(client);
    setIsClientDetailOpen(true);
  };

  const handleClientFormSubmit = (data: ClientInput) => {
    if (isEditMode && selectedClient) {
      updateClientMutation.mutate({ id: selectedClient.id, data });
    } else {
      createClientMutation.mutate(data);
    }
  };

  const handleDeleteClient = () => {
    if (selectedClient) {
      deleteClientMutation.mutate(selectedClient.id);
    }
  };

  const handleNewEstimate = (client?: ClientWithProjects) => {
    const clientId = client?.id || selectedClient?.id;
    console.log('🔍 Creating estimate for client ID:', clientId);
    if (clientId) {
      window.location.href = `/estimates/create?clientId=${clientId}`;
    } else {
      window.location.href = '/estimates/create';
    }
  };

  const handleExportClients = () => {
    const dataStr = JSON.stringify(clients, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'clients-export.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportSelected = () => {
    if (selectedClientIds.size === 0) return;
    const selected = clients.filter((c: Client) => selectedClientIds.has(c.id));
    const dataStr = JSON.stringify(selected, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'clients-selected-export.json';
    link.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Exported', description: `${selected.length} client(s) exported` });
    clearSelection();
  };

  const handleBulkDelete = async () => {
    if (selectedClientIds.size === 0) return;
    if (!confirm(`Delete ${selectedClientIds.size} client(s)? This cannot be undone.`)) return;
    const ids = Array.from(selectedClientIds);
    try {
      for (const id of ids) {
        await apiRequest('DELETE', `/api/protected/clients?id=${id}`);
      }
      queryClient.invalidateQueries({ queryKey: ['/api/protected/clients'] });
      setSelectedClientIds(new Set());
      toast({ title: 'Deleted', description: `${ids.length} client(s) deleted` });
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message || 'Failed to delete some clients', variant: 'destructive' });
    }
  };

  const handleImportClients = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedClients = JSON.parse(e.target?.result as string);
          console.log('Imported clients:', importedClients);
          toast({ title: 'Success', description: 'Clients imported successfully' });
        } catch (error) {
          toast({ title: 'Error', description: 'Invalid file format', variant: 'destructive' });
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-4 md:p-6 pt-24 flex flex-col items-center">
            <div className="w-full space-y-3">
              {/* Title + subtitle */}
              <div>
                <h1 className="text-2xl font-bold text-[var(--dash-text)]">Clients</h1>
                <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Manage user roles and access.</p>
              </div>

              {/* Toolbar: Search + Filter + Sort + Add */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--dash-text-muted)]" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-10 rounded-lg border-[var(--dash-border)] bg-[var(--dash-card)] text-[var(--dash-text)] placeholder:text-[var(--dash-text-muted)]"
                  />
                </div>
                <Button variant="outline" size="sm" className="h-10 rounded-lg border-[var(--dash-border)] text-[var(--dash-text)]">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
                <Button variant="outline" size="sm" className="h-10 rounded-lg border-[var(--dash-border)] text-[var(--dash-text)]">
                  Sort
                </Button>
                <div className="flex rounded-lg border border-[var(--dash-border)] p-0.5">
                  <button type="button" onClick={() => setViewMode('list')} className={`px-3 py-1.5 text-sm rounded-md ${viewMode === 'list' ? 'bg-[var(--dash-card)] shadow-sm font-medium' : 'text-[var(--dash-text-muted)]'}`}>Table</button>
                  <button type="button" onClick={() => setViewMode('cards')} className={`px-3 py-1.5 text-sm rounded-md ${viewMode === 'cards' ? 'bg-[var(--dash-card)] shadow-sm font-medium' : 'text-[var(--dash-text-muted)]'}`}>Cards</button>
                </div>
                <Button onClick={handleAddClient} className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white ml-auto">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add client
                </Button>
              </div>

              {/* Stat cards with deltas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-5 w-5 text-[var(--dash-text-muted)]" />
                  </div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{clients.length}</span>
                    <span className="dash-stat-delta positive">+{clients.filter((c: any) => new Date(c.createdAt).getMonth() === new Date().getMonth()).length}</span>
                  </div>
                  <div className="dash-stat-label">Total clients</div>
                </div>
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-5 w-5 text-[var(--dash-text-muted)]" />
                  </div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{clients.filter((c: ClientWithProjects) => getClientStatus(c) === 'Active').length}</span>
                    <span className="dash-stat-delta positive">+0</span>
                  </div>
                  <div className="dash-stat-label">Active clients</div>
                </div>
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-5 w-5 text-[var(--dash-text-muted)]" />
                  </div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{clients.filter((c: ClientWithProjects) => getClientStatus(c) === 'Inactive').length}</span>
                    <span className="dash-stat-delta negative">−0</span>
                  </div>
                  <div className="dash-stat-label">Inactive clients</div>
                </div>
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="h-5 w-5 text-[var(--dash-text-muted)]" />
                  </div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{clients.filter((c: ClientWithProjects) => getClientStatus(c) === 'Onboarding').length}</span>
                    <span className="dash-stat-delta positive">+{clients.filter((c: ClientWithProjects) => getClientStatus(c) === 'Onboarding').length}</span>
                  </div>
                  <div className="dash-stat-label">Onboarding clients</div>
                </div>
              </div>

            {/* Table card */}
            <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
              {/* Bulk actions bar */}
              {selectedClientIds.size > 0 && (
                <div className="px-6 py-3 border-b border-[var(--dash-border)] bg-green-500/5 flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-[var(--dash-text)]">{selectedClientIds.size} selected</span>
                  <Button variant="outline" size="sm" onClick={handleExportSelected} className="h-8 rounded-lg border-[var(--dash-border)]">
                    <Download className="h-4 w-4 mr-2" />
                    Export selected
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleBulkDelete} className="h-8 rounded-lg border-red-500/50 text-red-600 hover:bg-red-500 hover:text-white">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete selected
                  </Button>
                  <Button variant="ghost" size="sm" onClick={clearSelection} className="h-8 text-[var(--dash-text-muted)]">
                    <X className="h-4 w-4 mr-2" />
                    Clear selection
                  </Button>
                </div>
              )}

              {isLoadingClients ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div>
                  <p className="mt-2 text-slate-400">Loading clients...</p>
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-6">👥</div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">No Clients Found</div>
                  <div className="text-slate-600 dark:text-slate-400 text-lg mt-2 mb-6">
                    {searchQuery ? `No clients match "${searchQuery}"` : "Start by adding your first client"}
                  </div>
                  <Button className="remodra-button flex items-center justify-center gap-2 mx-auto" onClick={handleAddClient}>
                    <UserPlus className="h-5 w-5" />
                    Add First Client
                  </Button>
                </div>
              ) : viewMode === 'cards' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 p-6">
                  {filteredClients.map((client: ClientWithProjects) => (
                    <div key={client.id} className="group relative bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-2xl p-6 hover:shadow-2xl hover:border-green-500/50 transition-all duration-300 overflow-hidden">
                      {/* Checkbox for bulk actions */}
                      <div className="absolute top-4 right-4 z-10" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedClientIds.has(client.id)}
                          onCheckedChange={() => toggleSelectClient(client.id)}
                          className="border-2 border-slate-400 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                        />
                      </div>
                      {/* Gradient Background Effect */}
                      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-green-700/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700"></div>
                      
                      {/* Avatar and Header */}
                      <div className="relative flex items-start justify-between mb-6">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                              <span className="text-2xl font-bold text-white">
                                {client.firstName.charAt(0)}{client.lastName.charAt(0)}
                              </span>
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-4 border-white dark:border-slate-800"></div>
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-1">
                              {client.firstName} {client.lastName}
                            </h3>
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                <Mail className="h-3.5 w-3.5 text-green-500" />
                                <span className="truncate max-w-[200px]">{client.email}</span>
                              </div>
                              {client.phone && (
                                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                  <Phone className="h-3.5 w-3.5 text-green-500" />
                                  <span>{formatPhone(client.phone)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Address */}
                      {client.address && (
                        <div className="relative mb-4 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600">
                          <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                              {client.address}, {client.city}, {client.state} {client.zip}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {client.notes && (
                        <div className="relative mb-4">
                          <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 leading-relaxed">{client.notes}</p>
                        </div>
                      )}

                      {/* Projects Badge */}
                      <div className="relative mb-5">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500/10 to-green-700/10 dark:from-green-500/20 dark:to-green-700/20 rounded-lg border border-green-500/30">
                          <FileText className="h-4 w-4 text-green-600 dark:text-green-400" />
                          <span className="text-sm font-semibold text-green-700 dark:text-green-400">
                            {client.projects?.length || 0} {client.projects?.length === 1 ? 'Project' : 'Projects'}
                          </span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="relative flex flex-wrap gap-2 pt-4 border-t-2 border-slate-100 dark:border-slate-700">
                        <Button
                          variant="outline"
                          size="default"
                          onClick={() => handleViewClientDetails(client)}
                          className="flex-1 remodra-button-outline flex items-center justify-center gap-2 h-10 font-semibold hover:scale-105 transition-transform"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="default"
                          onClick={() => handleNewEstimate(client)}
                          className="flex-1 remodra-button-outline flex items-center justify-center gap-2 h-10 font-semibold hover:scale-105 transition-transform"
                        >
                          <FileText className="h-4 w-4" />
                          Estimate
                        </Button>
                        <Button
                          variant="outline"
                          size="default"
                          onClick={() => {
                            setSelectedClient(client);
                            handleDeleteClient();
                          }}
                          className="flex-1 sm:flex-none border-2 border-red-500/50 text-red-600 dark:text-red-400 hover:bg-red-600 hover:text-white hover:border-red-600 flex items-center justify-center gap-2 h-10 font-semibold hover:scale-105 transition-transform"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[var(--dash-border)]">
                          <th className="text-left py-2 px-3 w-10">
                            <Checkbox
                              checked={paginatedClients.length > 0 && paginatedClients.every((c) => selectedClientIds.has(c.id))}
                              onCheckedChange={() => {
                                const allSelected = paginatedClients.every((c) => selectedClientIds.has(c.id));
                                setSelectedClientIds((prev) => {
                                  const next = new Set(prev);
                                  if (allSelected) paginatedClients.forEach((c) => next.delete(c.id));
                                  else paginatedClients.forEach((c) => next.add(c.id));
                                  return next;
                                });
                              }}
                              className="border-[var(--dash-border)] data-[state=checked]:bg-[var(--dash-accent)]"
                            />
                          </th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">
                            <button type="button" className="flex items-center gap-1 hover:text-[var(--dash-text)]" onClick={() => toggleSort('name')}>
                              Name
                              {sortBy === 'name' ? (sortDir === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />) : <ChevronUp className="h-4 w-4 opacity-30" />}
                            </button>
                          </th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Phone</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">
                            <button type="button" className="flex items-center gap-1 hover:text-[var(--dash-text)]" onClick={() => toggleSort('projects')}>
                              Projects
                              {sortBy === 'projects' ? (sortDir === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />) : <ChevronUp className="h-4 w-4 opacity-30" />}
                            </button>
                          </th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">
                            <button type="button" className="flex items-center gap-1 hover:text-[var(--dash-text)]" onClick={() => toggleSort('createdAt')}>
                              Join date
                              {sortBy === 'createdAt' ? (sortDir === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />) : <ChevronUp className="h-4 w-4 opacity-30" />}
                            </button>
                          </th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Status</th>
                          <th className="w-10 py-2 px-3" />
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedClients.map((client: ClientWithProjects) => {
                          const status = getClientStatus(client);
                          return (
                            <tr key={client.id} className="border-b border-[var(--dash-border)] hover:bg-slate-50/80">
                              <td className="py-2 px-3">
                                <Checkbox
                                  checked={selectedClientIds.has(client.id)}
                                  onCheckedChange={() => toggleSelectClient(client.id)}
                                  className="border-[var(--dash-border)] data-[state=checked]:bg-[var(--dash-accent)]"
                                />
                              </td>
                              <td className="py-2 px-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-full bg-[var(--dash-accent)] flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                                    {client.firstName.charAt(0)}{client.lastName.charAt(0)}
                                  </div>
                                  <div>
                                    <div className="font-semibold text-[var(--dash-text)]">{client.firstName} {client.lastName}</div>
                                    <div className="text-xs text-[var(--dash-text-muted)]">{client.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="py-2 px-3">
                                <span className="text-sm text-[var(--dash-text)]">{formatPhone(client.phone) || '—'}</span>
                              </td>
                              <td className="py-2 px-3 text-sm text-[var(--dash-text)]">{client.projects?.length ?? 0}</td>
                              <td className="py-2 px-3 text-sm text-[var(--dash-text-muted)]">{formatJoinDate(client.createdAt)}</td>
                              <td className="py-2 px-3">
                                <span className={`dash-pill ${status === 'Active' ? 'dash-pill-active' : status === 'Inactive' ? 'dash-pill-inactive' : 'dash-pill-onboarding'}`}>
                                  {status}
                                </span>
                              </td>
                              <td className="py-2 px-3">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      <MoreVertical className="h-4 w-4 text-[var(--dash-text-muted)]" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48 rounded-lg border-[var(--dash-border)] bg-[var(--dash-card)]">
                                    <DropdownMenuItem onClick={() => handleViewClientDetails(client)} className="cursor-pointer">
                                      <Eye className="h-4 w-4 mr-2" />
                                      View
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => { setSelectedClient(client); handleEditClient(); }} className="cursor-pointer">
                                      <FileText className="h-4 w-4 mr-2" />
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleNewEstimate(client)} className="cursor-pointer">
                                      <FileText className="h-4 w-4 mr-2" />
                                      New estimate
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => { setSelectedClient(client); handleDeleteClient(); }} className="cursor-pointer text-red-600">
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 border-t border-[var(--dash-border)] bg-[var(--dash-bg)]">
                    <div className="flex items-center gap-2 text-sm text-[var(--dash-text-muted)]">
                      <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                        <SelectTrigger className="w-[120px] h-9 rounded-lg border-[var(--dash-border)]">
                          <SelectValue placeholder="50 records" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10 records</SelectItem>
                          <SelectItem value="25">25 records</SelectItem>
                          <SelectItem value="50">50 records</SelectItem>
                          <SelectItem value="100">100 records</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="ml-2">
                        {(currentPage - 1) * pageSize + 1} – {Math.min(currentPage * pageSize, sortedClients.length)} of {sortedClients.length}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" className="h-9 w-9 p-0 rounded-lg" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1}>←</Button>
                      {(() => {
                        const pages: number[] = [];
                        const show = Math.min(5, totalPages);
                        let start = currentPage <= 3 ? 1 : Math.min(currentPage - 2, totalPages - show + 1);
                        start = Math.max(1, start);
                        for (let i = 0; i < show; i++) pages.push(start + i);
                        return pages.map((p) => (
                          <Button key={p} variant={p === currentPage ? 'default' : 'outline'} size="sm" className="h-9 w-9 p-0 rounded-lg" onClick={() => setPage(p)}>{p}</Button>
                        ));
                      })()}
                      <Button variant="outline" size="sm" className="h-9 w-9 p-0 rounded-lg" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>→</Button>
                    </div>
                  </div>
                </>
              )}
            </div>
            </div>
          </main>
        </div>
      </div>

      {/* Hidden file input for import */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".json"
        className="hidden"
      />

      {/* Client Detail Dialog */}
      <Dialog open={isClientDetailOpen} onOpenChange={setIsClientDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : 'Client Details'}
            </DialogTitle>
            <DialogDescription>
              View and manage client information, projects, and portal access
            </DialogDescription>
          </DialogHeader>
          {selectedClient && (
            <ClientDetail 
              client={selectedClient}
              onEdit={handleEditClient}
              onDelete={handleDeleteClient}
              onNewEstimate={() => handleNewEstimate()}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Client Form Dialog */}
      <Dialog open={isClientFormOpen} onOpenChange={setIsClientFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? "Edit Client" : "Add New Client"}
            </DialogTitle>
          </DialogHeader>
          <ClientForm 
            client={isEditMode && selectedClient ? {
              id: selectedClient.id,
              firstName: selectedClient.firstName,
              lastName: selectedClient.lastName,
              email: selectedClient.email,
              phone: selectedClient.phone,
              address: selectedClient.address,
              city: selectedClient.city,
              state: selectedClient.state,
              zip: selectedClient.zip,
              notes: selectedClient.notes,
              createdAt: selectedClient.createdAt
            } : undefined}
            onSubmit={handleClientFormSubmit}
            isSubmitting={createClientMutation.isPending || updateClientMutation.isPending}
            onCancel={() => setIsClientFormOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}